import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, KeyRound, Building2, MapPin, User } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

function Field({ label, value }) {
  return (
    <div className="border-b border-border py-2.5 flex justify-between gap-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value ?? "-"}</span>
    </div>
  );
}

export default function LandlordDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [landlord, setLandlord] = useState(null);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [l, p] = await Promise.all([api.get(`/landlords/${id}`), api.get(`/landlords/${id}/properties`)]);
      setLandlord(l.data); setProps(p.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPinFn = async () => {
    if (!(pin.length === 6)) { toast.error(t("暗証番号は6桁で入力してください")); return; }
    setSaving(true);
    try {
      await api.post(`/landlords/${id}/set-pin`, { pin });
      toast.success(t("暗証番号を設定しました。貸主ポータルにログインできます。"));
      setPin(""); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (!landlord) return <div className="text-muted-foreground">{t("貸主が見つかりません")}</div>;

  const isCorp = landlord.account_type === "corporate";
  const displayName = landlord.name || landlord.company_name || "-";

  return (
    <div className="animate-fade-in" data-testid="landlord-detail">
      <Button variant="ghost" onClick={() => navigate("/landlords")} className="mb-4 -ml-2" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1.5" />{t("貸主一覧へ戻る")}
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center">
          {isCorp ? <Building2 className="w-6 h-6 text-brand" /> : <User className="w-6 h-6 text-brand" />}
        </div>
        <div>
          <h1 className="font-display text-2xl font-medium">{displayName}</h1>
          <div className="text-sm text-muted-foreground">{isCorp ? t("法人") : t("個人")}・{t("コード")} {landlord.login_code || "-"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-3">{t("基本情報")}</h3>
          {isCorp ? (
            <>
              <Field label={t("法人名")} value={landlord.company_name} />
              <Field label={t("法人番号")} value={landlord.registration_number} />
              <Field label={t("代表者")} value={[landlord.rep_last_name, landlord.rep_first_name].filter(Boolean).join(" ") || landlord.representative_name} />
            </>
          ) : (
            <>
              <Field label={t("氏名")} value={landlord.name} />
              <Field label={t("フリガナ")} value={landlord.name_kana} />
              <Field label={t("生年月日")} value={landlord.birth_date} />
            </>
          )}
          <Field label={t("電話番号")} value={landlord.phone} />
          <Field label={t("メール")} value={landlord.email} />
          <Field label={t("郵便番号")} value={landlord.postal_code} />
          <Field label={t("住所")} value={landlord.current_address || [landlord.prefecture, landlord.city, landlord.address_line, landlord.building].filter(Boolean).join("")} />
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-1.5"><KeyRound className="w-4 h-4 text-brand" />{t("ポータルログイン")}</h3>
          <Field label={t("ログインコード")} value={landlord.login_code} />
          <Field label={t("状態")} value={landlord.portal_active ? t("有効") : t("未設定")} />
          <div className="mt-4">
            <label className="text-xs text-muted-foreground mb-1.5 block">{t("暗証番号を設定 / リセット（6桁）")}</label>
            <div className="flex gap-2">
              <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder={t("6桁の数字")}
                className="flex-1 h-10 px-3 rounded-sm border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="landlord-pin-input" />
              <Button onClick={setPinFn} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="landlord-set-pin">
                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{t("設定")}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">{t("設定後、貸主は「/landlord/login」からコードと暗証番号でログインし、詳細情報を自己登録できます。")}</p>
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-brand" />{t("所有物件")}（{props.length}{t("件")}）</h3>
          {props.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("物件管理で物件の「貸主」に本貸主を設定すると、ここに表示されます。")}</p>
          ) : props.map((p) => (
            <div key={p.id} className="border-b border-border py-2.5 cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/properties/${p.id}`)} data-testid={`landlord-prop-${p.id}`}>
              <div className="text-sm font-medium">{p.name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{p.address || "-"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
